import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { useAuth } from './AuthContext'
import {
  getCart,
  addCartItem,
  updateCartItem,
  removeCartItem,
  clearCartApi,
} from '../services/shopApi'

const CartContext = createContext(null)

export function CartProvider({ children }) {
  const { user, accessToken } = useAuth()
  const [cart, setCart] = useState(null)
  const [cartLoading, setCartLoading] = useState(false)
  const [addingId, setAddingId] = useState(null)

  const refreshCart = useCallback(async () => {
    if (!accessToken) { setCart(null); return }
    setCartLoading(true)
    try {
      const data = await getCart(accessToken)
      setCart(data)
    } catch {
      // silent — cart may not exist yet
    } finally {
      setCartLoading(false)
    }
  }, [accessToken])

  useEffect(() => {
    if (user) refreshCart()
    else setCart(null)
  }, [user, refreshCart])

  const cartCount = cart?.items?.length ?? 0

  async function addToCart(tire, quantity = 4) {
    if (!accessToken) throw new Error('Not authenticated')
    const unitPrice = parseFloat(String(tire.price || 0).replace(/[^0-9.]/g, '')) || 0
    const productId = tire.partNum
    setAddingId(tire.partNum)
    try {
      const data = await addCartItem(accessToken, {
        haist_product_id: productId,
        brand: tire.brand,
        model: tire.model,
        size: tire.size,
        load_speed: tire.loadSpeed || '',
        sidewall: tire.sidewall || '',
        tire_type: tire.special || '',
        warranty: tire.warranty || null,
        unit_price: unitPrice,
        quantity,
      })
      setCart(data)
      return data
    } finally {
      setAddingId(null)
    }
  }

  async function updateQuantity(itemId, quantity) {
    if (!accessToken) return
    const data = await updateCartItem(accessToken, itemId, quantity)
    setCart(data)
    return data
  }

  async function removeItem(itemId) {
    if (!accessToken) return
    const data = await removeCartItem(accessToken, itemId)
    setCart(data)
    return data
  }

  async function clearCart() {
    if (!accessToken) return
    await clearCartApi(accessToken)
    await refreshCart()
  }

  return (
    <CartContext.Provider value={{
      cart,
      cartCount,
      cartLoading,
      addingId,
      addToCart,
      updateQuantity,
      removeItem,
      clearCart,
      refreshCart,
    }}>
      {children}
    </CartContext.Provider>
  )
}

export function useCart() {
  const ctx = useContext(CartContext)
  if (!ctx) throw new Error('useCart must be used inside CartProvider')
  return ctx
}
